import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  FaCopy,
  FaCheck,
  FaUser,
  FaGoogle,
  FaInstagram,
  FaYoutube,
  FaFacebook,
  FaLinkedin,
  FaPinterest,
  FaReddit,
} from "react-icons/fa";
import { SiTiktok } from "react-icons/si";
import { RiTwitterXFill } from "react-icons/ri";
import Modal from "../UI/Modal";

// Modal content containers
const ModalSection = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-family: "Playfair Display", serif;
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  color: var(--primary-color);
`;

const CreatorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Avatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--primary-color);
  background-color: var(--background-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    font-size: 2rem;
    color: var(--text-secondary);
  }
`;

const ProviderBadge = styled.div`
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--background-secondary);
  border: 1px solid var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 0.85rem;
    color: var(--primary-color);
  }
`;

const CreatorDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const CreatorName = styled.h3`
  font-size: 1.3rem;
  font-family: "Playfair Display", serif;
  margin: 0 0 0.25rem;
  background: linear-gradient(
    90deg,
    #bf953f,
    #fcf6ba,
    #b38728,
    #fbf5b7,
    #aa771c
  );
  background-size: 200% auto;
  color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ScriptDate = styled.p`
  color: var(--text-tertiary);
  font-size: 0.9rem;
  margin: 0;
`;

const ScriptContent = styled.div`
  position: relative;
  background-color: var(--background-tertiary);
  border-radius: var(--border-radius);
  border: 1px solid rgba(212, 175, 55, 0.15);
  padding: 1rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  background-color: transparent;
  color: var(--text-secondary);
  border: none;
  resize: none;
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.6;
  padding-right: 0.5rem;
  outline: none;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(212, 175, 55, 0.3);
    border-radius: 3px;
  }
`;

const CopyButtonWrapper = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 20;
`;

const IconButton = styled.button`
  background-color: transparent;
  color: #d4af37;
  border: none;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.15);
  }

  svg {
    font-size: 1.5rem;
    display: block;
  }
`;

const CopyFeedback = styled.div`
  position: absolute;
  top: -30px;
  right: 0;
  background: rgba(212, 175, 55, 0.9);
  color: #000;
  font-size: 0.8rem;
  padding: 0.35rem 0.75rem;
  border-radius: 4px;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  pointer-events: none;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }

  &:after {
    content: "";
    position: absolute;
    bottom: -5px;
    right: 13px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid rgba(212, 175, 55, 0.9);
  }
`;

const PromptText = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const PlatformsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const PlatformIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 1rem;
  background: rgba(0, 0, 0, 0.2);
  color: ${(props) => props.color || "var(--text-secondary)"};
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

// Platform configuration with brand colors
const platformConfig = {
  instagram: { icon: FaInstagram, color: "#E1306C" },
  youtube: { icon: FaYoutube, color: "#FF0000" },
  tiktok: { icon: SiTiktok, color: "#ffffff" },
  facebook: { icon: FaFacebook, color: "#1877F2" },
  twitter: { icon: RiTwitterXFill, color: "#ffffff" },
  x: { icon: RiTwitterXFill, color: "#ffffff" },
  linkedin: { icon: FaLinkedin, color: "#0A66C2" },
  pinterest: { icon: FaPinterest, color: "#E60023" },
  reddit: { icon: FaReddit, color: "#FF4500" },
};

const getProviderIcon = (provider) => {
  switch (provider) {
    case "google":
      return <FaGoogle />;
    default:
      return <FaUser />;
  }
};

const getPlatformIcons = (platforms) => {
  if (!platforms || !Array.isArray(platforms) || platforms.length === 0) {
    return null;
  }

  return (
    <PlatformsContainer>
      {platforms.map((platform, index) => {
        const lowerPlatform = platform.toLowerCase();
        const config = platformConfig[lowerPlatform] || {
          icon: FaUser,
          color: "#ccc",
        };
        const Icon = config.icon;

        return (
          <PlatformIcon key={index} color={config.color} title={platform}>
            <Icon />
          </PlatformIcon>
        );
      })}
    </PlatformsContainer>
  );
};

const ScriptModal = ({
  isOpen,
  onClose,
  script,
  creatorName,
  creatorAvatar,
  creatorProvider,
  formattedDate,
}) => {
  const [copied, setCopied] = useState(false);
  const [showCopyFeedback, setShowCopyFeedback] = useState(false);
  const [refinedPrompt, setRefinedPrompt] = useState("");

  useEffect(() => {
    // Check for refined prompt with hyphen
    if (script) {
      // Debug the data object to see exactly what we're getting
      console.log("Script object keys:", Object.keys(script));

      // Try multiple ways to access the field
      const prompt = script["refined-prompt"] || "";
      console.log("Refined prompt field (hyphenated):", prompt);

      // Check if the field exists but contains null
      if (
        script.hasOwnProperty("refined-prompt") &&
        script["refined-prompt"] === null
      ) {
        console.log("refined-prompt field exists but is null");
      }

      setRefinedPrompt(prompt);
    }
  }, [script]);

  if (!script) return null;

  const handleCopyScript = () => {
    navigator.clipboard.writeText(script.script || "");
    setCopied(true);
    setShowCopyFeedback(true);

    // Reset copied state after animation
    setTimeout(() => {
      setCopied(false);
    }, 2000);

    // Hide feedback after a bit longer
    setTimeout(() => {
      setShowCopyFeedback(false);
    }, 3000);
  };

  // Debug: log script data
  console.log("Full script object:", script);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Script Details">
      <CreatorInfo>
        <Avatar>
          {creatorAvatar ? (
            <img src={creatorAvatar} alt={creatorName} />
          ) : (
            <FaUser />
          )}
          {creatorProvider && (
            <ProviderBadge>{getProviderIcon(creatorProvider)}</ProviderBadge>
          )}
        </Avatar>
        <CreatorDetails>
          <CreatorName>{creatorName || "Unknown Creator"}</CreatorName>
          <ScriptDate>{formattedDate || "Unknown Date"}</ScriptDate>
          {script.platforms && getPlatformIcons(script.platforms)}
        </CreatorDetails>
      </CreatorInfo>

      <ModalSection>
        <SectionTitle>Original Prompt</SectionTitle>
        <ScriptContent>
          <PromptText>{script.prompt || "No prompt available"}</PromptText>
        </ScriptContent>
      </ModalSection>

      <ModalSection>
        <SectionTitle>Refined Prompt</SectionTitle>
        <ScriptContent>
          <PromptText>
            {refinedPrompt || "No refined prompt available"}
          </PromptText>
        </ScriptContent>
      </ModalSection>

      <ModalSection>
        <SectionTitle>Generated Script</SectionTitle>
        <ScriptContent>
          <TextArea readOnly value={script.script || "No script available"} />
          <CopyButtonWrapper>
            <IconButton onClick={handleCopyScript} title="Copy script">
              {copied ? <FaCheck /> : <FaCopy />}
            </IconButton>
            <CopyFeedback className={showCopyFeedback ? "visible" : ""}>
              Copied to clipboard
            </CopyFeedback>
          </CopyButtonWrapper>
        </ScriptContent>
      </ModalSection>
    </Modal>
  );
};

export default ScriptModal;
